<template>
  <div class="flex flex-col gap-2">
    <AutoComplete
      v-model="selectedMerchant"
      :suggestions="merchantSuggestions"
      @complete="searchMerchants"
      @item-select="handleMerchantSelect"
      @clear="handleClear"
      :optionLabel="item => (item ? item.displayName : '')"
      :loading="isSearching || isLoadingMerchant"
      placeholder="Merchant name or ID"
      :data-test-id="testId"
      showClear
      fluid
      size="small"
      inline
      autosize
    >
      <template #option="slotProps">
        <div
          v-if="slotProps.option"
          class="flex flex-col gap-1 py-1"
        >
          <span class="font-medium text-surface-900 dark:text-surface-0">
            {{ slotProps.option.Name }}
          </span>
          <span
            class="text-xs text-surface-500 dark:text-surface-400 font-mono"
          >
            {{ slotProps.option.Id }}
          </span>
        </div>
      </template>
    </AutoComplete>
    <p class="text-xs text-surface-500 dark:text-surface-400">
      Merchant IDs must match exactly. Merchant names can be partial matches.
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import AutoComplete from 'primevue/autocomplete'
import { useFrontegg } from '@frontegg/vue'
import { useDebounceFn } from '@vueuse/core'
import axios from 'axios'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import { normalizeError } from '@/util/errorNormalizer'
import type { CommonMerchantGetMerchantData } from '@/types/commonMerchantGetMerchantData'
import type {
  PagedMerchantResponse,
  MerchantQueryParams,
} from '@/types/commonMerchantGetMerchantData'

type MerchantSuggestion = CommonMerchantGetMerchantData & {
  displayName: string
}

interface Props {
  modelValue?: string | null
  testId?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  testId: 'merchant-autocomplete',
})

const emit = defineEmits<{
  'update:modelValue': [value: string | null]
}>()

const { authState } = useFrontegg()

// Local API method that doesn't affect shared commonMerchantApi state
async function getMerchantsLocal(
  params?: MerchantQueryParams,
): Promise<PagedMerchantResponse> {
  const token = authState.user?.accessToken
  const MERCHANT = API_ENDPOINTS.COMMON_MERCHANT_SERVICE

  return axios
    .get<PagedMerchantResponse>(MERCHANT.ALL_MERCHANTS, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        Id: params?.Id,
        Name: params?.Name,
        State: params?.State,
        page: params?.page ?? 0,
        size: params?.size ?? 15,
        sort: params?.sort,
      },
    })
    .then(({ data }) => data)
    .catch(err => {
      throw normalizeError(err)
    })
}

const merchantSuggestions = ref<MerchantSuggestion[]>([])
const isSearching = ref(false)
const isLoadingMerchant = ref(false)
const selectedMerchant = ref<MerchantSuggestion | null>(null)

// Load merchant when modelValue is set (e.g., from store)
async function loadMerchantById(merchantId: string) {
  if (!merchantId) {
    selectedMerchant.value = null
    return
  }

  isLoadingMerchant.value = true
  try {
    const response = await getMerchantsLocal({
      Id: merchantId,
      page: 0,
      size: 1,
    })

    if (response.Content.length > 0) {
      const merchant = response.Content[0]
      selectedMerchant.value = {
        ...merchant,
        displayName: `${merchant.Name}`,
      }
    } else {
      selectedMerchant.value = null
    }
  } catch (error) {
    console.error('Error loading merchant:', error)
    selectedMerchant.value = null
  } finally {
    isLoadingMerchant.value = false
  }
}

// Watch for modelValue changes
watch(
  () => props.modelValue,
  async newValue => {
    if (
      newValue &&
      (!selectedMerchant.value || selectedMerchant.value.Id !== newValue)
    ) {
      await loadMerchantById(newValue)
    } else if (!newValue) {
      selectedMerchant.value = null
    }
  },
  { immediate: true },
)

// Search merchants with debouncing - LIKE for names, EQUALS for IDs
const searchMerchants = useDebounceFn(async (event: { query: string }) => {
  const query = event.query?.trim()

  // Clear suggestions if query is empty or too short
  if (!query || query.length < 2) {
    merchantSuggestions.value = []
    return
  }

  isSearching.value = true
  try {
    // Determine if query is an ID (UUIDs are typically 36 chars)
    const queryIsId = query.length >= 36
    const searchParams = queryIsId ? { Id: query } : { Name: query }

    // Search with limited results for autocomplete
    const response = await getMerchantsLocal({
      ...searchParams,
      page: 0,
      size: 10, // Limit to 10 suggestions for autocomplete
    })

    // Map results to suggestions
    merchantSuggestions.value = response.Content.map(merchant => ({
      ...merchant,
      displayName: `${merchant.Name}`,
    }))
  } catch (error) {
    console.error('Error searching merchants:', error)
    merchantSuggestions.value = []
  } finally {
    isSearching.value = false
  }
}, 300)

function handleMerchantSelect(event: { value: MerchantSuggestion }) {
  selectedMerchant.value = event.value
  emit('update:modelValue', event.value.Id)
}

function handleClear() {
  selectedMerchant.value = null
  emit('update:modelValue', null)
}
</script>
